import React, { useRef, useState } from 'react';
import { FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import { useSpeechRecognition, isSpeechSupported } from './useSpeechRecognition';
import { DEFAULT_LANG, nextLang, getLangShortLabel } from './languages';
import { writeText } from './voiceWrite';
import './MicButton.css';

/**
 * MicButton — a self-contained voice-dictation button placed next to an
 * existing form input. It renders ONLY the mic + a per-field language toggle;
 * it does not own the input. On a final transcript it writes the (optionally
 * parsed) text into the sibling <input>/<textarea> found within the same
 * Bootstrap `.input-group`, driving the field's existing onChange handler.
 *
 * Functional components render fine inside class components, so this drops
 * cleanly into the class-based registration form.
 *
 * Controlled fields can't use that DOM route: they have no `.input-group`
 * wrapper to search, and their value lives in React state. Passing
 * `onTranscript` hands the text back to the caller instead, which is what the
 * clinical free-text fields do so they can append rather than overwrite.
 *
 * Props:
 *   label          {string}   accessible label of the field (for aria/title)
 *   long           {boolean}  continuous mode (for address / free-text fields)
 *   parser         {function} optional transcript -> value cleanup (e.g. cleanName)
 *   onTranscript   {function} optional (text) => void; replaces the DOM-write path
 *   showLangToggle {boolean}  render the per-field language chip (default true)
 *   silenceMs      {number}   pause that ends a `long` session (default 3s)
 */
export default function MicButton({
  label,
  long = false,
  parser,
  onTranscript,
  showLangToggle = true,
  silenceMs,
}) {
  const btnRef = useRef(null);
  const [lang, setLang] = useState(DEFAULT_LANG);

  const findInput = () => {
    const group = btnRef.current && btnRef.current.closest('.input-group');
    if (!group) return null;
    return group.querySelector('input, textarea');
  };

  const { start, stop, isListening, interim, elapsed } = useSpeechRecognition({
    lang,
    long,
    silenceMs,
    onResult: (text) => {
      const value = parser ? parser(text) : text;
      if (onTranscript) {
        onTranscript(value);
        return;
      }
      writeText(findInput(), value);
    },
  });

  // Graceful degradation: render nothing on unsupported browsers.
  if (!isSpeechSupported) return null;

  return (
    <span className="vf-mic-wrap">
      <button
        type="button"
        ref={btnRef}
        className={`vf-mic ${isListening ? 'active' : ''}`}
        onClick={isListening ? stop : start}
        aria-label={`Dictate ${label}`}
        title={`Dictate ${label}${long ? ' (up to 60s)' : ''}`}
        tabIndex={-1}
      >
        {isListening ? <FaMicrophoneSlash /> : <FaMicrophone />}
        {long && isListening && <span className="vf-mic-timer">{elapsed}s</span>}
      </button>
      {showLangToggle && (
        <button
          type="button"
          className="vf-lang-toggle"
          onClick={() => setLang((c) => nextLang(c))}
          aria-label={`Change dictation language for ${label}, currently ${getLangShortLabel(lang)}`}
          title={`Dictation language: ${getLangShortLabel(lang)} (click to change)`}
          tabIndex={-1}
        >
          {getLangShortLabel(lang)}
        </button>
      )}
      {isListening && interim && (
        <span className="vf-mic-live" title={interim}>{interim}</span>
      )}
    </span>
  );
}
